import { Table } from 'antd';
import type { TableColumnsType, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { format } from 'date-fns';
import { isNull, isUndefined } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import 'styles/ant-design.css';
import { getDynamicTableHeight } from 'utils';

interface CustomTableProps<T> {
  columns: TableColumnsType<T>;
  dataSource: T[];
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onShowSizeChange: (_: number, size: number) => void;
  onChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[]
  ) => void;
  onRow?: (record: T) => {
    onClick?: () => void;
    className?: string;
  };
  loading?: boolean;
}

const CustomTable = <T,>({
  columns,
  dataSource,
  currentPage,
  pageSize,
  onPageChange,
  onShowSizeChange,
  onChange,
  onRow,
  total,
  loading,
}: CustomTableProps<T>) => {
  const { t } = useTranslation();

  const enhancedColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      render: (
        data: string | boolean | Date | undefined | null,
        record: T,
        index: number
      ) => {
        if (col.render) {
          return col.render(data, record, index);
        }

        if (isUndefined(data) || isNull(data)) {
          return '-';
        }
        if (typeof data === 'boolean') {
          return data ? t('general.yes') : t('general.no');
        }

        if (data instanceof Date) {
          return format(data, 'dd/MM/yyyy');
        }

        return data;
      },
    }));
  }, [columns, t]);

  return (
    <Table
      columns={enhancedColumns}
      dataSource={dataSource}
      pagination={{
        current: currentPage,
        pageSize,
        total,
        onChange: onPageChange,
        onShowSizeChange,
        showSizeChanger: true,
        showTotal: (total, range) =>
          t('general.paginationFooter', {
            from: range[0],
            to: range[1],
            total,
          }),
      }}
      scroll={{ x: 'max-content', y: getDynamicTableHeight(500, 200) }}
      locale={{
        emptyText: 'No data available',
      }}
      bordered={true}
      className="custom-table custom-pagination"
      onChange={onChange}
      onRow={onRow}
      loading={loading}
    />
  );
};

export default CustomTable;
